import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { leadApi } from '../../services/api';
import { useApp } from '../../context/AppContext';
import { nicheConfig } from '../../theme';

const niches = Object.keys(nicheConfig);

const QuickAddLeadCard = ({ onLeadAdded }) => {
  const { showSnackbar } = useApp();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    instagramHandle: '',
    businessName: '',
    niche: '',
    city: '',
    stateProvince: '',
    source: 'manual',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.instagramHandle || !formData.niche) {
      showSnackbar('Instagram handle and niche are required', 'error');
      return;
    }

    setLoading(true);
    try {
      await leadApi.create(formData);
      showSnackbar('Lead added successfully!', 'success');
      setFormData({
        instagramHandle: '',
        businessName: '',
        niche: '',
        city: '',
        stateProvince: '',
        source: 'manual',
      });
      setExpanded(false);
      if (onLeadAdded) onLeadAdded();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to add lead', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AddIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Quick Add Lead
            </Typography>
          </Box>
          <IconButton size="small" sx={{ p: 0.5 }}>
            {expanded ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1.5 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instagram Handle"
                  name="instagramHandle"
                  value={formData.instagramHandle}
                  onChange={handleChange}
                  placeholder="@username"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Niche"
                  name="niche"
                  value={formData.niche}
                  onChange={handleChange}
                  required
                >
                  {niches.map((niche) => (
                    <MenuItem key={niche} value={niche}>
                      {nicheConfig[niche].emoji} {nicheConfig[niche].label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  name="stateProvince"
                  value={formData.stateProvince}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={<AddIcon />}
                >
                  {loading ? 'Adding...' : 'Add Lead'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default QuickAddLeadCard;
